import React, { lazy, Suspense, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, IconNames, ToastVariants } from '@dotcom/crown-ui';
import classNames from 'classnames';
import { useAnalyticsContext } from '../../context/analytics-context';
import { setIsSendToMe, setIsToasterOpen, useAppContext } from '../../context/app-context';
import { useDataContext } from '../../context/data-context/use-data-context';
import {
  AddressForm as AddressFormType,
  AddressTypeCode,
  AddressTypes,
  FormFieldAddress,
} from '../../global-types/addresses';
import { EnvelopeType } from '../../global-types/envelope';
import { useFeatureFlags } from '../../hooks';
import { useAddressNavigation } from '../../hooks/use-address-navigation';
import useAddressSelection from '../../hooks/use-address-selection';
import { useFormFieldGenerator } from '../../hooks/use-form-field-generator';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { useMultiAddresses } from '../../hooks/use-multi-addresses';
import { updateProject } from '../../services/customization';
import { buildOneToOneAddresses } from '../../utils/build-one-to-one-addresses';
import { useEnvelopeSelection } from '../../views/select-envelope/hooks/use-envelope-selection';
import { Contact } from '../address-book';
import { EditAddressDialog } from '../edit-address-dialog/edit-address-dialog';
import { FormFieldsGenerator } from '../form-fields-generator/form-fields-generator';
import { QuantityChangeDialog } from '../quantity-change-dialog/quantity-change-dialog';
import { RecipientAddressesDialog } from '../recipient-addresses-dialog/recipient-addresses-dialog';
import { RecipientAddressesVariant } from '../recipient-addresses-dialog/recipient-addresses-dialog-types';
import { AddressFormProps, DeliveryModes } from './address-form-types';
import styles from './address-form.module.scss';
import { AddressBookButton } from './fragments/address-book-button';
import { AddressFormFooterButtons } from './fragments/address-form-footer-buttons';
import { EnvelopeRadioGroup } from './fragments/envelope-radio-group';
import { Header } from './fragments/header';
import { useShouldHideForm } from './hooks/use-should-hide-form';
import { useUpdateAddress } from './hooks/use-update-address';
import { addressFormFieldList } from './utils/address-form-field-list';
import { setAddressFormValues } from './utils/set-address-form-values';
import { RadioCard } from '../radio-card/radio-card';

/**
 * Lazy Address Book since it's
 * only needed for US Address cards
 */
const QuickAddressDialogComponent = lazy(() =>
  import('../../components/quick-address-dialog-container/quick-address-dialog-container').then((module) => ({
    default: module.QuickAddressDialogContainer,
  })),
);

/**
 * Suspense fallback component
 */
const AddressBookLoader = () => <div data-testid="quick-address-dialog-loader"></div>;

export const AddressForm = ({
  addClass,
  formHandlers,
  onSkipValidation,
  getAddressesProps,
  footerButtonsProps,
}: AddressFormProps): React.ReactElement => {
  // Contexts
  const {
    initializedDataState: {
      data: initializedData,
      isRecipientAddress: isRecipientAddress1to1,
      addressData,
      isUS,
      isUK,
    },
    setAddressData,
  } = useDataContext();
  const {
    appState: { isSendToMe, productQuantity },
    appDispatch,
  } = useAppContext();
  const { trackSelectedQuickAddress } = useAnalyticsContext();

  // Local State
  const [isQuickAddressOpen, setIsQuickAddressOpen] = useState(false);
  const [currentQuantity, setCurrentQuantity] = useState<number>(Number(productQuantity));
  const [isQuantityIncreaseDialogOpen, setIsQuantityIncreaseDialogOpen] = useState(false);
  const { IS_MULT_ADDRESS_ENABLED } = useFeatureFlags();
  const classes = classNames(styles['address-form-wrapper'], addClass);

  // hooks
  const { t } = useTranslation();
  const isOneToMany = useIsOneToMany();
  const { selectedEnvelope } = useEnvelopeSelection();
  const { navigateWithParams } = useAddressNavigation();
  const { isRecipientAddress: isRecipient1AddresstoM, handleResetCount } = useAddressSelection();

  const projectId = initializedData?.project_id;

  const isRecipientAddress = isOneToMany ? isRecipient1AddresstoM : isRecipientAddress1to1;

  const formTitle1to1 = isRecipientAddress1to1 ? t('addressForm.recipientTitle') : t('addressForm.returnTitle');
  const formTitle1toM = isRecipientAddress
    ? `${t('addressForm.step', { step: 3 })} ${t('addressForm.recipientTitle')}`
    : `${t('addressForm.step', { step: 2 })} ${t('addressForm.returnTitle')}`;
  const formTitle = isOneToMany ? formTitle1toM : formTitle1to1;

  // form values for the address form
  const { getValues, setValue, register, watch } = formHandlers;
  const {
    isOpen: isDialogOpen,
    setIsOpen: setRecipientAddressesDialogOpen,
    isLoading: isGetAddressesLoading,
  } = getAddressesProps;

  // If the product quantity changes, check if the current quantity is less than the new product quantity
  useEffect(() => {
    if (currentQuantity !== null && currentQuantity < Number(productQuantity) && IS_MULT_ADDRESS_ENABLED) {
      setIsQuantityIncreaseDialogOpen(true);
    }
    setCurrentQuantity(Number(productQuantity));
  }, [IS_MULT_ADDRESS_ENABLED, currentQuantity, productQuantity]);

  const envelope = watch('envelope') as EnvelopeType;

  const { shouldHideForm } = useShouldHideForm(envelope, selectedEnvelope);

  if (isSendToMe) {
    setValue('send_to', 'me');
    // If false, form defaults to 'them'
  }

  // Scroll address form to top when user moves betweeen recipient and return address
  useEffect(() => {
    document.querySelector('form')?.scrollIntoView();
  }, [formTitle]);

  // this function is called when the user clicks on the submit button from the AddressBook
  const handleAddressBookSubmit = (newAddress: Contact) => {
    trackSelectedQuickAddress(formTitle);
    const addressTypeCode = watch('address_type_code');
    // preventing the user from adding an address without a valid address type code
    if (addressTypeCode !== AddressTypeCode.RECIPIENT && addressTypeCode !== AddressTypeCode.SENDER) {
      setIsToasterOpen(appDispatch, {
        title: t('addressForm.addressBook.error.title'),
        children: t('addressForm.addressBook.error.message'),
        variant: ToastVariants.Error,
      });
      return;
    }
    setAddressFormValues(newAddress, setValue);
    // get the selected address values from the form
    const addressFormValues = getValues();

    // update the initialization data state with the new recipient address
    const isRecipientAddress = addressTypeCode === AddressTypeCode.RECIPIENT;
    const currentAddressType = isRecipientAddress ? AddressTypes.RECIPIENT : AddressTypes.SENDER;

    const mappedAddress = buildOneToOneAddresses(addressFormValues);
    if (mappedAddress) {
      setAddressData({
        [currentAddressType]: { ...addressData[`${currentAddressType}`], ...mappedAddress[String(currentAddressType)] },
      });
    }
  };

  const handleDeliveryModeChange = () => {
    // Store send_to in state in case user leaves/returns
    const isSendToMeChecked = getValues('send_to') === 'me';
    const deliveryMode = isSendToMeChecked ? DeliveryModes.ShipToMe : DeliveryModes.MailToRecipient;
    const data = {
      delivery_mode: deliveryMode,
    };
    setIsSendToMe(appDispatch, isSendToMeChecked);
    updateProject(data, projectId);
  };

  const { generatedFormFields: selectedFormFields } = useFormFieldGenerator(
    formHandlers as unknown as UseFormReturn<FormFieldAddress>,
    'address',
    addressFormFieldList,
  );

  const handleOnOpenAddressBook = () => {
    setIsQuickAddressOpen(true);
  };
  const handleOnCloseAddressBook = () => {
    setIsQuickAddressOpen(false);
  };

  // values and handlers for the edit address dialog
  const {
    formValues,
    handleUpdateReturnRecipientAddress,
    handleCloseEditAddress,
    dialogState: { openEditDialog, setOpenEditDialog },
  } = useUpdateAddress(setRecipientAddressesDialogOpen);
  const {
    handleSubmit,
    reset: resetRecipientAddressForm,
    formState: { isValid },
  } = formValues;

  const { shouldUseEnvelopePrintingOptions } = useMultiAddresses();

  const onRecipientAddressConfirm = () => {
    navigateWithParams('/card/customization/preview', envelope);
    handleResetCount();
  };

  const onQuantityIncreaseDialogActionButtonClick = () => {
    setIsQuantityIncreaseDialogOpen(false);
  };

  const onQuantityIncreaseDialogCancelButtonClick = () => {
    setIsQuantityIncreaseDialogOpen(false);
    setRecipientAddressesDialogOpen(true);
  };

  return (
    <div className={classes}>
      <form className={styles['address-form']} data-testid="address-form">
        <div tabIndex={-1} className={styles['address-form-content']}>
          <Header formTitle={formTitle} isRecipientAddress={isRecipientAddress} />

          {!shouldUseEnvelopePrintingOptions && isOneToMany && (
            <EnvelopeRadioGroup register={register} envelope={envelope} />
          )}

          {isUK && (
            <div role="radiogroup" aria-labelledby="radio_button_group" className={styles['radio-cards-container']}>
              <RadioCard
                domId="send-to-me-radio-card"
                helperText="Send to Me"
                register={register('send_to', {
                  required: true,
                  onChange: handleDeliveryModeChange,
                })}
                addClass={styles['radio-card']}
                value="me"
              >
                Sent to you with a spare envelope
              </RadioCard>
              <RadioCard
                domId="send-to-them-radio-card"
                helperText="Send to Them"
                register={register('send_to', {
                  required: true,
                  onChange: handleDeliveryModeChange,
                })}
                addClass={styles['radio-card']}
                value="them"
              >
                Sent directly to the recipient
              </RadioCard>
            </div>
          )}

          {!shouldHideForm && (
            <>
              {isUS && <AddressBookButton click={handleOnOpenAddressBook} />}
              <FormFieldsGenerator selectedFormFields={selectedFormFields} register={register} setValue={setValue} />
            </>
          )}

          {!isGetAddressesLoading && isDialogOpen && (
            <RecipientAddressesDialog
              isOpen={isDialogOpen}
              resetRecipientAddressForm={resetRecipientAddressForm}
              setRecipientAddressesDialogOpen={setRecipientAddressesDialogOpen}
              setOpenEditDialog={setOpenEditDialog}
              variant={RecipientAddressesVariant.Address}
              onActionButtonClick={onRecipientAddressConfirm}
              onSecondaryActionButtonClick={() => setRecipientAddressesDialogOpen(false)}
            />
          )}

          {openEditDialog && (
            <EditAddressDialog
              title={t('editRecipientAddressDialog.title')}
              accentIcon={IconNames.AddressBookBold}
              isEditAddressVisible={openEditDialog}
              onCloseEditAddress={handleCloseEditAddress}
              formHandlers={formValues as unknown as UseFormReturn<AddressFormType>}
              actionButton={
                <Button
                  testId={'edit-recipient-address-dialog-update-button'}
                  onClick={handleSubmit(handleUpdateReturnRecipientAddress)}
                  disabled={!isValid}
                >
                  {t('editRecipientAddressDialog.actionButton')}
                </Button>
              }
            />
          )}
          <AddressFormFooterButtons
            oneToOneProps={footerButtonsProps.oneToOneProps}
            oneToManyProps={footerButtonsProps.oneToManyProps}
          />
        </div>
      </form>
      {isUS && isQuickAddressOpen && (
        <div data-testid={'quick-address-dialog-container'}>
          <Suspense fallback={<AddressBookLoader />}>
            <QuickAddressDialogComponent
              formTitle={formTitle}
              handleAddressBookSubmit={handleAddressBookSubmit}
              isAddressBookOpen={isQuickAddressOpen}
              onCloseAddressBook={handleOnCloseAddressBook}
              onSkipValidation={onSkipValidation}
              formHandlers={formHandlers}
              isRecipientAddress={isRecipientAddress}
            />
          </Suspense>
        </div>
      )}
      {isUS && isQuantityIncreaseDialogOpen && (
        <QuantityChangeDialog
          isOpen={isQuantityIncreaseDialogOpen}
          onClose={() => setIsQuantityIncreaseDialogOpen(false)}
          actionButton={onQuantityIncreaseDialogActionButtonClick}
          cancelButton={onQuantityIncreaseDialogCancelButtonClick}
          isIncrementing={true}
        />
      )}
    </div>
  );
};
